import React, { useState, useMemo, useRef } from "react";
import { toast } from "sonner";
import { usePendingOrders, PendingOrder } from "../hooks/usePendingOrders";
import { useMarkets } from "../hooks/useMarkets";
import { ExchangeClient } from "../sdk";
import {
  formatPriceFromSatoshi,
  formatQuantityFromSatoshi,
} from "../sdk/utils/display";
import {
  LoadingMessage,
  ErrorMessage,
  EmptyMessage,
  WarningMessage,
  LayoutHeader,
  TableHeaderCell,
  TableHeaderCellStatic,
  CancelButton,
} from "./smartlayout/fragments";
import {
  DEFAULT_BASE_URL,
  DEFAULT_HEIGHT_PENDING_ORDERS,
  DEFAULT_REFRESH_INTERVAL,
  SORT_DIRECTION_ASC,
  SORT_DIRECTION_DESC,
  ORDER_SORT_FIELD_SYMBOL,
  ORDER_SORT_FIELD_SIDE,
  ORDER_SORT_FIELD_TYPE,
  ORDER_SORT_FIELD_QUANTITY,
  ORDER_SORT_FIELD_PRICE,
  ORDER_SORT_FIELD_FILLED_QUANTITY,
  ORDER_SORT_FIELD_STATUS,
  ORDER_VIEW_MODE_PENDING,
  ORDER_VIEW_MODE_HISTORY,
  ORDER_PENDING_STATUSES,
  ORDER_STATUS_COLOR_PENDING,
  ORDER_STATUS_COLOR_OPEN,
  ORDER_STATUS_COLOR_FILLED,
  ORDER_STATUS_COLOR_CANCELED,
  ORDER_STATUS_COLOR_REJECTED,
  ORDER_STATUS_COLOR_UNKNOWN,
  ORDER_SIDE_COLOR_BUY_LONG,
  ORDER_SIDE_COLOR_SELL_SHORT,
  ORDER_GRID_TEMPLATE_COLUMNS,
  ORDER_LOADING_MESSAGE,
  ORDER_EMPTY_MESSAGE_PENDING,
  ORDER_EMPTY_MESSAGE_HISTORY,
  ORDER_TITLE_PENDING,
  ORDER_TITLE_HISTORY,
  ORDER_WARNING_WALLET_REQUIRED,
  ORDER_TAB_LABEL_PENDING,
  ORDER_TAB_LABEL_HISTORY,
  ORDER_TYPE_FALLBACK,
  ORDER_STATUS_UNKNOWN,
  ORDER_TYPE_DISPLAY_LIMIT,
  ORDER_TYPE_DISPLAY_MARKET,
  ORDER_TYPE_PREFIXED_LIMIT,
  ORDER_TYPE_PREFIXED_MARKET,
  ORDER_TYPE_PREFIX,
  ORDER_SIDE_LONG,
  ORDER_SIDE_SHORT,
  ORDER_SIDE_BUY,
  ORDER_SIDE_SELL,
  OrderSortField,
  OrderSortDirection,
  OrderViewMode,
} from "./smartlayout/constants";
import "./smartlayout/layout.css";

interface PendingOrdersProps {
  baseURL?: string;
  wsURL?: string;
  authToken?: string;
  walletAddress?: string;
  mnemonic?: string; // Required for canceling orders
  height?: string;
  refreshInterval?: number;
}

function PendingOrders({
  baseURL = DEFAULT_BASE_URL,
  wsURL,
  authToken,
  walletAddress,
  mnemonic,
  height = DEFAULT_HEIGHT_PENDING_ORDERS,
  refreshInterval = DEFAULT_REFRESH_INTERVAL,
}: PendingOrdersProps): JSX.Element {
  const { orders, loading, error, refresh } = usePendingOrders({
    baseURL,
    wsURL,
    authToken,
    walletAddress,
    refreshInterval,
  });

  const exchangeClientRef = useRef<ExchangeClient | null>(null);
  const [cancelingOrderId, setCancelingOrderId] = useState<string | null>(null);

  // Debug: Log component props on render
  React.useEffect(() => {
    console.log("[PendingOrders] Component rendered with props", {
      hasMnemonic: !!mnemonic,
      mnemonicLength: mnemonic?.length,
      mnemonicType: typeof mnemonic,
      walletAddress,
      ordersCount: orders?.length,
      baseURL,
    });
  }, [mnemonic, walletAddress, orders?.length, baseURL]);

  // Get markets for symbol mapping (optional - will fallback to hardcoded if not available)
  const { getSymbolByMarketId } = useMarkets({
    baseURL,
    sessionToken: authToken,
    autoFetchMarkets: false, // Don't auto-fetch, just use if already loaded
  });

  const [viewMode, setViewMode] = useState<OrderViewMode>(ORDER_VIEW_MODE_PENDING);
  const [sortField, setSortField] = useState<OrderSortField | null>(null);
  const [sortDirection, setSortDirection] = useState<OrderSortDirection>(SORT_DIRECTION_DESC);

  // Filter orders based on view mode
  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      const status = (order.status || "").toUpperCase();
      const normalizedStatus = status.startsWith("ORDER_STATUS_")
        ? status
        : `ORDER_STATUS_${status}`;

      if (viewMode === ORDER_VIEW_MODE_PENDING) {
        return ORDER_PENDING_STATUSES.some(
          (pendingStatus) =>
            status === pendingStatus.toUpperCase() ||
            normalizedStatus === pendingStatus.toUpperCase(),
        );
      } else {
        // History view: all orders that are NOT pending
        return !ORDER_PENDING_STATUSES.some(
          (pendingStatus) =>
            status === pendingStatus.toUpperCase() ||
            normalizedStatus === pendingStatus.toUpperCase(),
        );
      }
    });
  }, [orders, viewMode]);

  // Sort orders
  const sortedOrders = useMemo(() => {
    if (!sortField) return filteredOrders;

    return [...filteredOrders].sort((a, b) => {
      let aValue: number | string = 0;
      let bValue: number | string = 0;

      switch (sortField) {
        case ORDER_SORT_FIELD_SYMBOL:
          aValue = a.symbol || "";
          bValue = b.symbol || "";
          break;
        case ORDER_SORT_FIELD_SIDE:
          aValue = a.side || "";
          bValue = b.side || "";
          break;
        case ORDER_SORT_FIELD_TYPE:
          aValue = getOrderType(a.type);
          bValue = getOrderType(b.type);
          break;
        case ORDER_SORT_FIELD_QUANTITY:
          aValue = a.quantity || 0;
          bValue = b.quantity || 0;
          break;
        case ORDER_SORT_FIELD_PRICE:
          aValue = a.price || 0;
          bValue = b.price || 0;
          break;
        case ORDER_SORT_FIELD_FILLED_QUANTITY:
          aValue = a.filledQuantity || 0;
          bValue = b.filledQuantity || 0;
          break;
        case ORDER_SORT_FIELD_STATUS:
          aValue = a.status || "";
          bValue = b.status || "";
          break;
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortDirection === SORT_DIRECTION_ASC
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      const numA = Number(aValue);
      const numB = Number(bValue);

      return sortDirection === SORT_DIRECTION_ASC ? numA - numB : numB - numA;
    });
  }, [filteredOrders, sortField, sortDirection]);

  const handleSort = (field: OrderSortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === SORT_DIRECTION_ASC ? SORT_DIRECTION_DESC : SORT_DIRECTION_ASC);
    } else {
      setSortField(field);
      setSortDirection(SORT_DIRECTION_DESC);
    }
  };

  // Helper: Check if order is leveraged (futures/perps)
  const isLeveragedOrder = (order: PendingOrder): boolean => {
    const side = (order.side || "").toUpperCase();
    return side === ORDER_SIDE_LONG || side === ORDER_SIDE_SHORT;
  };

  // Helper: Get side display (LONG/SHORT for leveraged, BUY/SELL for spot)
  const getOrderSide = (order: PendingOrder): string => {
    const normalizedSide = (order.side || "").toUpperCase();

    if (isLeveragedOrder(order)) {
      // For leveraged orders, ensure LONG/SHORT
      return normalizedSide === ORDER_SIDE_BUY || normalizedSide === ORDER_SIDE_LONG
        ? ORDER_SIDE_LONG
        : ORDER_SIDE_SHORT;
    } else {
      // For spot orders, ensure BUY/SELL
      return normalizedSide === ORDER_SIDE_LONG || normalizedSide === ORDER_SIDE_BUY
        ? ORDER_SIDE_BUY
        : ORDER_SIDE_SELL;
    }
  };

  // Use display utilities for formatting
  const formatPrice = formatPriceFromSatoshi;
  const formatQuantity = formatQuantityFromSatoshi;

  // Helper: Get short status with full name for tooltip
  const getShortStatus = (
    status: string | undefined,
  ): { short: string; full: string; color: string } => {
    const normalizedStatus = (status || "").toUpperCase();

    const statusMap: Record<
      string,
      { short: string; full: string; color: string }
    > = {
      ORDER_STATUS_PENDING: { short: "P", full: "Pending", color: ORDER_STATUS_COLOR_PENDING },
      PENDING: { short: "P", full: "Pending", color: ORDER_STATUS_COLOR_PENDING },
      ORDER_STATUS_OPEN: { short: "O", full: "Open", color: ORDER_STATUS_COLOR_OPEN },
      OPEN: { short: "O", full: "Open", color: ORDER_STATUS_COLOR_OPEN },
      NEW: { short: "N", full: "New", color: ORDER_STATUS_COLOR_OPEN },
      ORDER_STATUS_FILLED: { short: "F", full: "Filled", color: ORDER_STATUS_COLOR_FILLED },
      FILLED: { short: "F", full: "Filled", color: ORDER_STATUS_COLOR_FILLED },
      ORDER_STATUS_CANCELED: { short: "C", full: "Canceled", color: ORDER_STATUS_COLOR_CANCELED },
      CANCELED: { short: "C", full: "Canceled", color: ORDER_STATUS_COLOR_CANCELED },
      CANCELLED: { short: "C", full: "Canceled", color: ORDER_STATUS_COLOR_CANCELED },
      ORDER_STATUS_REJECTED: { short: "R", full: "Rejected", color: ORDER_STATUS_COLOR_REJECTED },
      REJECTED: { short: "R", full: "Rejected", color: ORDER_STATUS_COLOR_REJECTED },
      PARTIALLY_FILLED: {
        short: "PF",
        full: "Partially Filled",
        color: ORDER_STATUS_COLOR_PENDING,
      },
    };

    // Try exact match first
    if (statusMap[normalizedStatus]) {
      return statusMap[normalizedStatus];
    }

    // Try matching with ORDER_STATUS_ prefix
    const withPrefix = normalizedStatus.startsWith("ORDER_STATUS_")
      ? normalizedStatus
      : `ORDER_STATUS_${normalizedStatus}`;
    if (statusMap[withPrefix]) {
      return statusMap[withPrefix];
    }

    // Default fallback
    return { short: "?", full: status || ORDER_STATUS_UNKNOWN, color: ORDER_STATUS_COLOR_UNKNOWN };
  };

  // Helper: Get side color
  const getSideColor = (side: string): string => {
    return (side === ORDER_SIDE_LONG || side === ORDER_SIDE_BUY) ? ORDER_SIDE_COLOR_BUY_LONG : ORDER_SIDE_COLOR_SELL_SHORT;
  };

  // Helper: Normalize order type (ORDER_TYPE_LIMIT -> LIMIT, ORDER_TYPE_MARKET -> MARKET)
  const getOrderType = (type: string | undefined): string => {
    if (!type) return ORDER_TYPE_FALLBACK;
    const normalizedType = type.toUpperCase();

    // Handle prefixed types from API
    if (normalizedType === ORDER_TYPE_PREFIXED_LIMIT || normalizedType === ORDER_TYPE_DISPLAY_LIMIT) {
      return ORDER_TYPE_DISPLAY_LIMIT;
    }
    if (normalizedType === ORDER_TYPE_PREFIXED_MARKET || normalizedType === ORDER_TYPE_DISPLAY_MARKET) {
      return ORDER_TYPE_DISPLAY_MARKET;
    }

    // Fallback: remove ORDER_TYPE_ prefix if present, otherwise return as-is
    if (normalizedType.startsWith(ORDER_TYPE_PREFIX)) {
      return normalizedType.replace(ORDER_TYPE_PREFIX, "");
    }

    return normalizedType;
  };

  const handleCancelOrder = async (order: (typeof orders)[0]) => {
    console.log("[PendingOrders.handleCancelOrder] Function called", {
      order,
      orderId: order.orderId || order.id,
      marketId: order.marketId,
      hasMnemonic: !!mnemonic,
      mnemonicType: typeof mnemonic,
      mnemonicLength: mnemonic?.length,
      timestamp: new Date().toISOString(),
    });

    // Get order ID and market ID first for better error messages
    const orderId = order.orderId || order.id;
    const marketId = order.marketId;

    // Validate order data
    if (!orderId) {
      console.error("Cancel order failed: Order ID is missing", order);
      toast.error("Order ID is missing", {
        description: "Cannot cancel order without a valid order ID.",
      });
      return;
    }

    if (!marketId) {
      console.error("Cancel order failed: Market ID is missing", order);
      toast.error("Market ID is missing", {
        description: "Cannot cancel order without a valid market ID.",
      });
      return;
    }

    // Check if mnemonic is available with detailed logging
    if (!mnemonic) {
      console.warn("Cancel order failed: Mnemonic prop is not provided", {
        orderId,
        marketId,
        mnemonicProvided: false,
      });
      toast.error("Mnemonic required", {
        description: `Please provide the 'mnemonic' prop to the PendingOrders component. Order ID: ${orderId}`,
        duration: 5000,
      });
      return;
    }

    if (typeof mnemonic !== "string" || mnemonic.trim() === "") {
      console.warn("Cancel order failed: Mnemonic is empty or invalid", {
        orderId,
        marketId,
        mnemonicType: typeof mnemonic,
        mnemonicLength: mnemonic?.length,
      });
      toast.error("Invalid mnemonic", {
        description: `The mnemonic prop is empty or invalid. Please provide a valid mnemonic. Order ID: ${orderId}`,
        duration: 5000,
      });
      return;
    }

    // Prevent multiple simultaneous cancellations
    if (cancelingOrderId) {
      console.log("Cancel order skipped: Already canceling another order", {
        currentCancelingOrderId: cancelingOrderId,
        requestedOrderId: orderId,
      });
      toast.info("Cancellation in progress", {
        description: "Please wait for the current cancellation to complete.",
      });
      return; // Already canceling an order
    }

    console.log("Starting order cancellation", { orderId, marketId });
    setCancelingOrderId(orderId);

    try {
      // Initialize ExchangeClient if not already initialized
      if (!exchangeClientRef.current) {
        console.log("Initializing ExchangeClient for order cancellation");
        exchangeClientRef.current = new ExchangeClient({
          baseURL,
          mnemonic: mnemonic.trim(),
        });
        await exchangeClientRef.current.initialize();
        console.log("ExchangeClient initialized successfully");
      }

      // Cancel the order
      console.log("Calling cancelOrder API", { orderId, marketId });
      const response = await exchangeClientRef.current.cancelOrder({
        orderId,
        marketId,
      });

      console.log("Cancel order API response", response);

      // Check if cancellation was successful
      // The API may return different response structures, so we check for success indicators
      const isSuccess = 
        response?.success === true ||
        (response?.result === "success") ||
        (response && !response.error && !response.message);

      if (isSuccess) {
        console.log(`Order ${orderId} canceled successfully`);
        toast.success("Order canceled successfully", {
          description: `Order ${orderId} has been canceled.`,
        });
        // Refresh orders to update the UI
        if (refresh) {
          await refresh();
        }
      } else {
        const errorMsg = 
          response?.error || 
          response?.message || 
          response?.response?.error ||
          "Failed to cancel order";
        console.error("Cancel order failed: API returned error", {
          orderId,
          marketId,
          response,
        });
        throw new Error(errorMsg);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      console.error(`Failed to cancel order ${orderId}:`, {
        error,
        errorMessage,
        orderId,
        marketId,
        stack: error instanceof Error ? error.stack : undefined,
      });
      toast.error("Failed to cancel order", {
        description: `${errorMessage}. Order ID: ${orderId}`,
        duration: 6000,
      });
    } finally {
      setCancelingOrderId(null);
    }
  };

  // Show appropriate message when wallet address is not available
  if (!walletAddress || walletAddress.trim() === "") {
    return (
      <WarningMessage message={ORDER_WARNING_WALLET_REQUIRED} />
    );
  }

  if (loading) {
    return <LoadingMessage message={ORDER_LOADING_MESSAGE} />;
  }

  if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    <div className="layout-container" style={{ height }}>
      {/* Header */}
      <LayoutHeader
        title={viewMode === ORDER_VIEW_MODE_PENDING ? ORDER_TITLE_PENDING : ORDER_TITLE_HISTORY}
      >
        <div className="btn-tab">
          <span style={{ fontSize: "0.7rem" }}>▼</span>
          <span>Current Symbol</span>
        </div>
        <div
          onClick={() =>
            setViewMode(viewMode === ORDER_VIEW_MODE_PENDING ? ORDER_VIEW_MODE_HISTORY : ORDER_VIEW_MODE_PENDING)
          }
          className={`btn-tab ${viewMode === ORDER_VIEW_MODE_HISTORY ? "active" : ""}`}
        >
          <img src="/transfer-net.svg" />
          <span>{viewMode === ORDER_VIEW_MODE_PENDING ? ORDER_TAB_LABEL_HISTORY : ORDER_TAB_LABEL_PENDING}</span>
        </div>
      </LayoutHeader>

      {/* Table */}
      <div className="table-container shrinkable">
        {/* Table Header */}
        <div
          className="table-header"
          style={{
            gridTemplateColumns: ORDER_GRID_TEMPLATE_COLUMNS,
          }}
        >
          <TableHeaderCell
            field={ORDER_SORT_FIELD_SYMBOL}
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            Symbol
          </TableHeaderCell>
          <TableHeaderCell
            field={ORDER_SORT_FIELD_SIDE}
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            Side
          </TableHeaderCell>
          <TableHeaderCell
            field={ORDER_SORT_FIELD_TYPE}
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            Type
          </TableHeaderCell>
          <TableHeaderCell
            field={ORDER_SORT_FIELD_QUANTITY}
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            Quantity
          </TableHeaderCell>
          <TableHeaderCell
            field={ORDER_SORT_FIELD_PRICE}
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            Price
          </TableHeaderCell>
          <TableHeaderCell
            field={ORDER_SORT_FIELD_FILLED_QUANTITY}
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            Filled
          </TableHeaderCell>
          <TableHeaderCell
            field={ORDER_SORT_FIELD_STATUS}
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            Status
          </TableHeaderCell>
          <TableHeaderCellStatic center>Actions</TableHeaderCellStatic>
        </div>

        {/* Table Body */}
        {sortedOrders.length === 0 ? (
          <EmptyMessage
            message={
              viewMode === ORDER_VIEW_MODE_PENDING
                ? ORDER_EMPTY_MESSAGE_PENDING
                : ORDER_EMPTY_MESSAGE_HISTORY
            }
          />
        ) : (
          sortedOrders.map((order, index) => {
            const side = getOrderSide(order);
            const sideColor = getSideColor(side);
            const {
              short: shortStatus,
              full: fullStatus,
              color: statusColor,
            } = getShortStatus(order.status);
            const marketSymbol = order.marketId
              ? getSymbolByMarketId(order.marketId) ||
                order.symbol || `m-${order.marketId}`
              : order.symbol || "N/A";

            return (
              <div
                key={`order-${order.id || order.orderId || index}`}
                className={`table-row ${index % 2 === 0 ? "even" : ""}`}
                style={{
                  gridTemplateColumns: ORDER_GRID_TEMPLATE_COLUMNS,
                }}
              >
                {/* Symbol */}
                <div
                  className="table-cell primary"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <span>{marketSymbol}</span>
                </div>

                {/* Side */}
                <div
                  className="table-cell center"
                  style={{ color: sideColor, fontWeight: 500 }}
                >
                  {side}
                </div>

                {/* Type */}
                <div className="table-cell center">
                  {getOrderType(order.type)}
                </div>

                {/* Quantity */}
                <div className="table-cell right monospace">
                  {formatQuantity(order.quantity)}
                </div>

                {/* Price */}
                <div className="table-cell right monospace">
                  {formatPrice(order.price)}
                </div>

                {/* Filled */}
                <div className="table-cell right monospace">
                  {formatQuantity(order.filledQuantity)} /{" "}
                  {formatQuantity(order.quantity)}
                </div>

                {/* Status */}
                <div
                  className="table-cell center"
                  style={{
                    color: statusColor,
                    fontWeight: 600,
                    cursor: "help",
                    fontSize: "0.9rem",
                  }}
                  title={fullStatus}
                >
                  {shortStatus}
                </div>

                {/* Actions */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                  onClick={(e) => {
                    console.log("[PendingOrders] Actions div clicked", {
                      target: e.target,
                      currentTarget: e.currentTarget,
                      orderId: order.orderId || order.id,
                    });
                  }}
                >
                  {viewMode === ORDER_VIEW_MODE_PENDING ? (
                    <>
                      {(!mnemonic || mnemonic.trim() === "") && (
                        <span
                          style={{
                            fontSize: "0.7rem",
                            color: "#ffc107",
                            cursor: "help",
                          }}
                          title="Mnemonic required to cancel orders"
                        >
                          ⚠️
                        </span>
                      )}
                      <div
                        onClick={(e) => {
                          console.log(
                            "[PendingOrders] Cancel button wrapper clicked",
                            {
                              target: e.target,
                              currentTarget: e.currentTarget,
                              orderId: order.orderId || order.id,
                            }
                          );
                        }}
                        onMouseDown={(e) => {
                          console.log(
                            "[PendingOrders] Cancel button wrapper mouse down",
                            {
                              target: e.target,
                              orderId: order.orderId || order.id,
                            }
                          );
                        }}
                      >
                        {(() => {
                          const isDisabled =
                            !mnemonic ||
                            mnemonic.trim() === "" ||
                            cancelingOrderId === (order.orderId || order.id) ||
                            !!cancelingOrderId;

                          return (
                            <CancelButton
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                console.log(
                                  "[PendingOrders] Cancel button onClick fired",
                                  {
                                    orderId: order.orderId || order.id,
                                    marketId: order.marketId,
                                    hasMnemonic: !!mnemonic,
                                    mnemonicLength: mnemonic?.length,
                                    mnemonicType: typeof mnemonic,
                                    cancelingOrderId,
                                    isDisabled,
                                    event: e,
                                  }
                                );
                                if (!isDisabled) {
                                  handleCancelOrder(order);
                                } else {
                                  console.warn(
                                    "[PendingOrders] Cancel order prevented - button is disabled",
                                    {
                                      orderId: order.orderId || order.id,
                                      isDisabled,
                                    }
                                  );
                                }
                              }}
                              disabled={isDisabled}
                            />
                          );
                        })()}
                      </div>
                    </>
                  ) : (
                    <span className="color-text-dim">—</span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// Memoize component to prevent re-renders when props haven't changed
export default React.memo(PendingOrders);
